import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '../data/siteData';
import StoreButtons from '../components/StoreButtons';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.fade-up');
    if (!els.length) return;
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

/* ── Shared phone components ─────────────────────────────────────────────────── */
function HomeScreenMockup() {
  const cats = [
    { name: 'Mess', color: '#C8F135', pct: 72, amt: '₹2,160' },
    { name: 'Food Outside', color: '#F97316', pct: 55, amt: '₹825' },
    { name: 'Transport', color: '#3B82F6', pct: 38, amt: '₹304' },
    { name: 'Entertainment', color: '#EC4899', pct: 88, amt: '₹704' },
    { name: 'Savings', color: '#10B981', pct: 100, amt: '₹1,000' },
  ];
  return (
    <div className="phone-mockup">
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-topbar">
          <div><div className="mock-greeting">Good morning,</div><div className="mock-name">Arjun 👋</div></div>
          <div className="mock-avatar">A</div>
        </div>
        <div className="mock-donut-section">
          <div className="mock-donut-wrap">
            <div className="mock-donut"/>
            <div className="mock-donut-hole">
              <div className="mock-budget-label">Spent</div>
              <div className="mock-budget-amount">₹4,993</div>
              <div className="mock-budget-sub">of ₹10k</div>
            </div>
          </div>
        </div>
        <div className="mock-categories">
          {cats.map(c => (
            <div className="mock-cat-row" key={c.name}>
              <div className="mock-cat-dot" style={{ background: c.color }}/>
              <div className="mock-cat-name">{c.name}</div>
              <div className="mock-cat-bar-wrap"><div className="mock-cat-bar" style={{ width: `${c.pct}%`, background: c.color }}/></div>
              <div className="mock-cat-amount">{c.amt}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LogExpenseMockup() {
  const chips = ['Mess', 'Food', 'Transport', 'Hostel', 'Care'];
  const keys = ['1','2','3','4','5','6','7','8','9','.','0','✓'];
  return (
    <div className="phone-mockup">
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-log-title">Log Expense</div>
        <div className="mock-cat-select">
          {chips.map((c, i) => <div key={c} className={`mock-cat-chip ${i === 2 ? 'active' : ''}`}>{c}</div>)}
        </div>
        <div className="mock-amount-display">
          <span className="mock-amount-prefix">₹</span>
          <span className="mock-amount-value">48</span>
        </div>
        <div className="mock-numpad">
          {keys.map((k, i) => (
            <div key={i} className={`mock-numpad-key ${k === '✓' ? 'mock-numpad-key-lime' : ''}`}>{k}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InsightsMockup() {
  const bars = [
    { label: 'Mon', h: 30 }, { label: 'Tue', h: 55 }, { label: 'Wed', h: 42 },
    { label: 'Thu', h: 70, active: true }, { label: 'Fri', h: 25 }, { label: 'Sat', h: 85 }, { label: 'Sun', h: 10 },
  ];
  return (
    <div className="phone-mockup">
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div className="mock-insights-title">This Week</div>
        <div className="mock-bar-chart">
          {bars.map(b => (
            <div className="mock-bar-col" key={b.label}>
              <div className={`mock-bar ${b.active ? 'active' : ''}`} style={{ height: `${b.h}%` }}/>
              <div className="mock-bar-label">{b.label}</div>
            </div>
          ))}
        </div>
        <div className="mock-insight-cards">
          <div className="mock-insight-card"><span className="mock-insight-label">Highest spend</span><span className="mock-insight-val">Food Outside</span></div>
          <div className="mock-insight-card"><span className="mock-insight-label">Week total</span><span className="mock-insight-val">₹1,840</span></div>
        </div>
        <div className="mock-streak-section">
          <div className="mock-streak-icon">🔥</div>
          <div><div className="mock-streak-count">14</div><div className="mock-streak-label">Day streak</div></div>
        </div>
      </div>
    </div>
  );
}

function OnboardingMockup() {
  return (
    <div className="phone-mockup">
      <div className="phone-notch"><div className="phone-notch-speaker"/><div className="phone-notch-camera"/></div>
      <div className="phone-screen">
        <div className="mock-status-bar"><span>9:41</span><span>●●●</span></div>
        <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, background: '#C8F135', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', fontWeight: 800, fontSize: 20, color: '#080810' }}>K</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Welcome to Klar</div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>Set up your budget in 2 min</div>
        </div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 4 }}>Your name</div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '7px 10px', fontSize: 11, color: 'var(--text-primary)', marginBottom: 8, border: '1px solid rgba(255,255,255,0.08)' }}>Priya Raghavan</div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 4 }}>Phone number</div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '7px 10px', fontSize: 11, color: 'var(--text-primary)', marginBottom: 8, border: '1px solid rgba(255,255,255,0.08)' }}>+91 98765 43210</div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 4 }}>Monthly budget (₹)</div>
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '7px 10px', fontSize: 11, color: 'var(--text-primary)', marginBottom: 14, border: '1px solid rgba(200,241,53,0.3)' }}>10,000</div>
        <div style={{ background: '#C8F135', borderRadius: 10, padding: '10px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#080810' }}>Continue →</div>
      </div>
    </div>
  );
}

/* ── Feature deep-dive data ─────────────────────────────────────────────────── */
const featureDeeps = [
  {
    id: 'budget',
    label: 'Budget Allocation',
    title: 'Split your budget across 8 real student categories.',
    desc: 'Klar starts with 8 categories that reflect how Indian college students actually spend: Mess, Food Outside, Transport, Hostel, Personal Care, Entertainment, Savings, and Misc. You set the amounts, Klar tracks the balance.',
    bullets: [
      'Visual donut ring shows spending at a glance',
      'Colour-coded categories for instant recognition',
      'Remaining balance updates in real-time as you log',
      'Set once per month — no repetitive setup',
    ],
    mockup: <HomeScreenMockup />,
    reverse: false,
  },
  {
    id: 'log',
    label: 'Expense Logging',
    title: 'Log any expense in under 5 seconds.',
    desc: 'A custom numpad replaces the standard keyboard. Pick your category, type the amount, tap confirm. That\'s it. No memo fields, no receipt scans, no friction — just log and move on.',
    bullets: [
      'Custom numpad optimised for rupee amounts',
      'One-tap category selection',
      'Note field for optional context',
      'All logs stored locally — no internet needed',
    ],
    mockup: <LogExpenseMockup />,
    reverse: true,
  },
  {
    id: 'insights',
    label: 'Insights & Streaks',
    title: 'Day, week, and month trends — plus a streak that keeps you honest.',
    desc: 'The Insights screen breaks your spending down by day, week, or month, shows your highest-spend category, and tracks your daily usage streak. Compare periods and spot the leaks before they become habits.',
    bullets: [
      'Switch between daily, weekly, and monthly views',
      'Period-over-period comparisons spot trends',
      'Daily streak count builds the logging habit',
      'Category rankings show where money leaks',
    ],
    mockup: <InsightsMockup />,
    reverse: false,
  },
  {
    id: 'money',
    label: 'Income, Goals & Reminders',
    title: 'Track what comes in, save toward what matters, never miss a bill.',
    desc: 'Klar is more than expenses. Log pocket money, stipends, and side income to see your true cash flow, set savings goals you can actually watch grow, and add recurring bills so a payment never slips past you.',
    bullets: [
      'Income tracking with clear cash-flow view',
      'Savings goals with month-over-month progress',
      'Recurring bill reminders for rent, OTT, and subs',
      'Search and filter every transaction instantly',
    ],
    mockup: <HomeScreenMockup />,
    reverse: true,
  },
  {
    id: 'onboard',
    label: 'Onboarding',
    title: 'Sign in with Google. Done in 2 minutes.',
    desc: 'One Google sign-in, your name, phone number, and monthly budget. That\'s the entire setup. No email verification loops, no payment details, no demo videos to watch.',
    bullets: [
      'Google OAuth — one tap, no passwords',
      'Name and phone collection for personalisation',
      'Monthly budget entry with category slider',
      'Straight to your home screen, no tutorial wall',
    ],
    mockup: <OnboardingMockup />,
    reverse: false,
  },
];

/* ── Tech specs ─────────────────────────────────────────────────────────────── */
const techSpecs = [
  { label: 'Platforms', value: 'iOS · Android · Mac', desc: 'iPhone, iPad, Mac, and Android' },
  { label: 'Auth', value: 'Google Sign-In', desc: 'Sign in with Google, one tap' },
  { label: 'Data Storage', value: 'On-device', desc: 'Your financial data stays on your phone' },
  { label: 'Security', value: 'App Lock', desc: 'Face ID, fingerprint, or PIN lock' },
  { label: 'Data Export', value: 'CSV', desc: 'Export your full history anytime' },
  { label: 'Price', value: 'Free Forever', desc: 'No ads, no subscription, no premium tier' },
];

/* ── ProductPage ────────────────────────────────────────────────────────────── */
export default function ProductPage() {
  useScrollReveal();

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="product-hero">
        <div className="inner-hero-bg"/>
        <div className="container">
          <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
            <div className="section-label fade-up">Product</div>
            <h1 className="text-display fade-up delay-1">
              Built for the <span className="text-gradient">Indian Campus</span> lifestyle.
            </h1>
            <p className="text-body-lg mt-6 fade-up delay-2" style={{ maxWidth: 560, margin: '24px auto 40px' }}>
              Every feature in Klar was designed around the real spending patterns of Indian college students — hostellers, day scholars, and everyone in between. Now live and free on iPhone and Android.
            </p>
            <div className="fade-up delay-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
              <StoreButtons className="center" size="lg" />
              <Link to="/student-journey" className="btn btn-ghost btn-sm">
                See Student Journey <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature deep-dives ───────────────────────────────────────────── */}
      {featureDeeps.map((f, idx) => (
        <section key={f.id} className="feature-deep-section">
          <div className="container">
            <div className={`feature-split fade-up ${f.reverse ? 'reverse' : ''}`}>
              <div>
                <span className="feature-detail-label">{f.label}</span>
                <h2 className="feature-detail-title">{f.title}</h2>
                <p className="feature-detail-desc">{f.desc}</p>
                <div className="feature-bullets">
                  {f.bullets.map(b => (
                    <div key={b} className="feature-bullet">
                      <div className="bullet-check"><CheckIcon /></div>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className="animate-floatSlow" style={{ animationDelay: `${idx * 0.8}s` }}>
                  {f.mockup}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ── Budget Categories ─────────────────────────────────────────────── */}
      <section className="section" style={{ background: 'var(--bg-alt)' }}>
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Budget Categories</div>
            <h2 className="text-headline">8 categories. Every student expense covered.</h2>
            <p className="text-body-lg mt-4">
              The categories are based on how real Indian college students actually spend. Each one is customisable to your budget.
            </p>
          </div>
          <div className="category-grid">
            {categories.map((cat, i) => (
              <div key={cat.name} className={`category-card fade-up delay-${(i % 4) + 1}`}>
                <div className="category-icon" style={{ background: cat.colorDim }}>
                  {cat.emoji}
                </div>
                <div className="category-name">{cat.name}</div>
                <div className="category-example">{cat.example}</div>
                <div className="category-amount">₹{cat.defaultBudget.toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: 'var(--text-sm)', color: 'var(--text-faint)', marginTop: 24 }}>
            * Default budget amounts shown. Every category is fully customisable.
          </p>
        </div>
      </section>

      {/* ── Technical details ────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="features-intro fade-up">
            <div className="section-label">Under the Hood</div>
            <h2 className="text-headline">Simple tech for a focused product.</h2>
            <p className="text-body-lg mt-4">
              Klar is a Flutter app. Fast, native, private. No servers storing your data, no subscriptions, no ads.
            </p>
          </div>
          <div className="tech-grid fade-up delay-2">
            {techSpecs.map(spec => (
              <div key={spec.label} className="tech-card">
                <div className="tech-card-label">{spec.label}</div>
                <div className="tech-card-value">{spec.value}</div>
                <div className="tech-card-desc">{spec.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <div className="cta-banner">
        <div className="container">
          <div className="cta-banner-content">
            <h2 className="cta-banner-headline">Ready to get Klar about your money?</h2>
            <p className="cta-banner-sub">
              Free to use. No bank access. Just you and your budget. Download it now.
            </p>
            <StoreButtons className="center" size="lg" />
          </div>
        </div>
      </div>
    </>
  );
}
